import { Controller } from '@hotwired/stimulus';

const SWAP_TIMEOUT_MS = 10000;
let swapQueue = Promise.resolve();
const queuedSwaps = new Set();

// Posts the chip, date or note the moment it changes, so the page below
// re-renders from the server with the new selection. One round trip keeps the
// offer copy, the prices and the button label honest without duplicating any
// of that maths in JavaScript.
//
// Move-box and interval cards with a confirm button still wire chips to
// #submit; when a cta target is present, submit only refills that button.
//
// A swap group (the order-issue chips) stays on the page: see #swap.
//
// Every auto-submitting group also renders a plain submit button. This
// controller hides it on connect, so the manual button is what the customer
// gets whenever the bundle is missing, stale or broken — not only when JS is
// switched off.
export default class extends Controller {
  static targets = ['manual', 'cta', 'customDateInput', 'customDateSubmit', 'customChip', 'noteSubmit'];
  static values = { swap: Boolean };

  connect() {
    this.submitting = false;
    this.manualTargets.forEach((button) => {
      button.hidden = true;
    });
    const note = this.element.querySelector('textarea');
    if (this.hasNoteSubmitTarget && note) this.noteSubmitTarget.disabled = note.value.trim() === '';
    if (this.swapValue) this.statusRegion();
  }

  /**
   * Enables the note's Submit, which the design keeps visible, once the note has text.
   *
   * @param {InputEvent} event the textarea's input event
   */
  toggleNoteSubmit(event) {
    if (this.hasNoteSubmitTarget) this.noteSubmitTarget.disabled = event.target.value.trim() === '';
  }

  // Refill the button from the chip the customer just picked.
  //
  // Which card this is comes from the placeholder left in data-template.
  // The server renders that string with the token still in it, and this
  // method only substitutes the token. It does not format dates or weeks.
  preview(event) {
    if (!this.hasCtaTarget) return;

    const template = this.ctaTarget.dataset.template;
    if (!template) return;

    // Move-box card. The template is "Move my box to %{date}"; only the short
    // date goes in the button, not the chip sublabel ("Arrives …").
    if (template.includes('%{date}')) {
      const date = event.target.dataset.shipDateLabel;
      if (!date) return;

      this.setCtaLabel(template.replace('%{date}', date));
      return;
    }

    // Interval card. A template like "Ship every %{weeks} weeks" takes the
    // week number from the radio's data-weeks; a fixed label stays as it is.
    const weeks = event.target.dataset.weeks;
    if (!weeks) return;

    if (template.includes('%{weeks}')) this.setCtaLabel(template.replace('%{weeks}', weeks));

    // The no-subscription final card repeats this interval count. The continue
    // link carries it, since picking a chip no longer reloads the page.
    const link = document.querySelector('.m-cancelv2__continue');
    if (!link) return;

    const url = new URL(link.href);
    url.searchParams.set('intervals', event.target.value);
    link.href = url.toString();
  }

  setCtaLabel(label) {
    if (this.ctaTarget.tagName === 'BUTTON') {
      this.ctaTarget.textContent = label;
    } else {
      this.ctaTarget.value = label;
    }
  }

  // Custom calendar pick on a move-box card. Inline confirm keeps it local; otherwise
  // the whole form posts to the reason URL like a chip change.
  // Inline confirm: the Custom chip is not in the selections[ship_dates] radio group.
  selectCustom() {
    this.uncheckWeekShipChips();
    if (this.hasCustomChipTarget) this.customChipTarget.checked = true;
    if (this.hasCtaTarget && this.hasCustomDateSubmitTarget && !this.customDateSubmitTarget.value) {
      this.ctaTarget.disabled = true;
    }
  }

  uncheckWeekShipChips() {
    this.element.querySelectorAll("input[type='radio'][name='selections[ship_dates]']").forEach((radio) => {
      radio.checked = false;
    });
  }

  pickShipDate(event) {
    const input = event.target;
    const iso = input.value?.trim();
    if (!iso) return;

    this.selectCustom();

    if (this.hasCustomDateSubmitTarget) {
      this.customDateSubmitTarget.value = iso;
      this.customDateSubmitTarget.disabled = false;
    }

    if (this.hasCtaTarget) {
      this.ctaTarget.disabled = false;
      const template = this.ctaTarget.dataset.template;
      if (template?.includes('%{date}')) {
        this.setCtaLabel(template.replace('%{date}', this.shortShipDate(iso, input)));
      }
      return;
    }

    this.submit(event);
  }

  shortShipDate(iso, input) {
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      date = new Date(`${iso}T12:00:00`);
    } else if (input?.value) {
      date = new Date(input.value);
    }
    if (!date || Number.isNaN(date.getTime())) return input?.value?.trim() || iso;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  submit(event) {
    // A note that did not change does not need a round trip.
    const field = event && event.target;
    if (field && field.tagName === 'TEXTAREA' && field.value === field.defaultValue) return;

    // Move-box and interval cards with a confirm button: refill the label only.
    if (this.hasCtaTarget) {
      if (event?.target?.name === 'selections[ship_dates]') {
        if (this.hasCustomChipTarget) this.customChipTarget.checked = false;
      }
      if (this.hasCustomDateSubmitTarget) {
        this.customDateSubmitTarget.disabled = true;
        this.customDateSubmitTarget.value = '';
      }
      this.ctaTarget.disabled = false;
      this.preview(event);
      return;
    }

    if (this.swapValue) {
      this.swap();
      return;
    }

    if (this.submitting) return;

    this.submitting = true;
    if (this.element.requestSubmit) {
      this.element.requestSubmit();
    } else {
      this.element.submit();
    }
  }

  /**
   * Queues one post of this form. swapQueue sends one post at a time across
   * every chip group, so the server saves picks in click order. Picks made
   * while a post waits ride along, since the form is read when it is sent.
   */
  swap() {
    if (queuedSwaps.has(this)) return;

    queuedSwaps.add(this);
    const run = swapQueue.then(() => this.sendSwap());
    swapQueue = run.catch(() => {});
  }

  /**
   * Posts the pick with fetch, which follows the redirect back to this page,
   * and swaps the new page in around this form, so the clicked chip keeps its
   * state and focus. The answer is dropped when any newer pick is queued
   * (queuedSwaps, in this group or another), or when the form left the page.
   * Any other answer (an error, a redirect elsewhere), a network failure or a
   * post slower than SWAP_TIMEOUT_MS loads the page in full, so one stalled
   * post cannot block the queue.
   */
  async sendSwap() {
    queuedSwaps.delete(this);
    if (!this.element.isConnected) return;

    let response;
    let html;
    try {
      response = await fetch(this.element.action, {
        method: 'POST',
        body: new FormData(this.element),
        headers: { Accept: 'text/html' },
        signal: AbortSignal.timeout?.(SWAP_TIMEOUT_MS),
      });
      html = await response.text();
    } catch {
      if (this.element.isConnected) this.element.submit();
      return;
    }
    if (queuedSwaps.size || !this.element.isConnected) return;

    const body = this.element.parentElement;
    const next = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('.m-cancelv2__body');
    const field = this.element.querySelector("input[type='radio']")?.name;
    const twin =
      field && next?.querySelector(`input[name='${field}']`)?.closest('form');
    const samePage =
      new URL(response.url).pathname === window.location.pathname;
    if (
      !response.ok ||
      !samePage ||
      !body?.matches('.m-cancelv2__body') ||
      twin?.parentElement !== next
    ) {
      window.location.href = response.url;
      return;
    }

    const nodes = Array.from(next.childNodes);
    const at = nodes.indexOf(twin);
    Array.from(body.childNodes).forEach((node) => {
      if (node !== this.element) node.remove();
    });
    this.element.before(...nodes.slice(0, at));
    this.element.after(...nodes.slice(at + 1));
    this.announce(body.querySelector('h1')?.textContent);
  }

  /**
   * Reads the new heading to screen readers.
   *
   * @param {string|undefined} text the heading after the swap
   */
  announce(text) {
    if (text) this.statusRegion().textContent = text.trim();
  }

  /**
   * The one shared status region, added on connect so it exists before the
   * first swap; a region added with its text is often not read.
   *
   * @returns {HTMLElement} the region
   */
  statusRegion() {
    let status = document.getElementById('cancel-chip-group-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'cancel-chip-group-status';
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      document.body.append(status);
    }
    return status;
  }
}
